// Shared formatting + period helpers for the Super Admin AI Usage / Credits screens.
//
// All AI usage cost is reported and displayed in USD. The backend meters cost in micro-USD
// (`microdollars_per_credit`, default $0.0001/credit) and exposes it as `approx_cost_usd`, so
// cost is always a US-dollar figure shown with `$`.
export const CREDIT_COST = {
  ratePerCredit: 0.0001, // USD per credit (config `microdollars_per_credit` / 1e6)
  currency: 'USD',
  symbol: '$',
};

export const creditsToCost = (credits, rate = CREDIT_COST.ratePerCredit) =>
  (Number(credits) || 0) * rate;

// Compact credit count, e.g. 142000 -> "142K", 4200 -> "4.2K".
export function formatCredits(credits, { compact = true } = {}) {
  const n = Number(credits) || 0;
  if (!compact || Math.abs(n) < 1000) return n.toLocaleString('en-GB');
  // Intl renders the compact suffix lower-case ("142k"); the design uses "142K".
  return new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
    .format(n)
    .toUpperCase();
}

// Formats a figure that is ALREADY in US dollars — the API meters cost itself and reports it as
// `approx_cost_usd` / `approx_total_cost_usd`, which is authoritative and should be shown in
// preference to multiplying credits by the rate.
export function formatUsd(value, { compact = false } = {}) {
  const opts = compact
    ? { notation: 'compact', maximumFractionDigits: 1 }
    : { minimumFractionDigits: 0, maximumFractionDigits: 2 };
  return `${CREDIT_COST.symbol}${new Intl.NumberFormat('en-GB', opts).format(Number(value) || 0)}`;
}

// Converts a CREDIT count to its dollar cost at the configured rate, then formats it.
export function formatCost(credits, { compact = false } = {}) {
  return formatUsd(creditsToCost(credits), { compact });
}

// Renders a value in the caller's chosen mode ('credit' | 'cost').
export const formatByMode = (credits, mode, opts) =>
  mode === 'cost' ? formatCost(credits, opts) : formatCredits(credits, opts);

// Percentage display. `decimals` keeps the grain the API actually reports: the credits API
// returns `pct` to two places (e.g. 6.02), and rounding to a whole number threw that away —
// small consumption collapsed to "0%" and 6.02% read as "6%". Trailing zeros are trimmed so a
// clean figure still renders as "83%", not "83.00%".
export function formatPercent(value, { of, decimals = 0 } = {}) {
  const pct = of ? ((Number(value) || 0) / of) * 100 : Number(value) || 0;
  if (!decimals) return `${Math.round(pct)}%`;
  const fixed = pct.toFixed(decimals);
  return `${Number(fixed)}%`;
}

// --- Periods (YYYY-MM, Europe/London) --------------------------------------

const LONDON = 'Europe/London';

// Current metering period as YYYY-MM in London time.
export function currentPeriod(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const year = parts.find((p) => p.type === 'year').value;
  const month = parts.find((p) => p.type === 'month').value;
  return `${year}-${month}`;
}

// Human label for a period, e.g. "2026-07" -> "July 2026".
export function monthLabel(period) {
  if (!period) return '';
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

// Descending list of periods from `from` up to (and including) `to`. Used to bound
// the month picker to when metering was switched on — no cost/credit backfill exists.
export function listPeriods(from, to = currentPeriod()) {
  if (!from) return [to];
  const out = [];
  let [y, m] = to.split('-').map(Number);
  const [fy, fm] = from.split('-').map(Number);
  while (y > fy || (y === fy && m >= fm)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1;
    if (m === 0) {
      m = 12;
      y -= 1;
    }
    if (out.length > 240) break; // safety
  }
  return out;
}
